import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  StyleSheet,
  ScrollView,
  Text,
  TouchableOpacity,
  View as RNView,
  Modal,
  Pressable,
  ActivityIndicator,
  RefreshControl,
  useColorScheme,
  TextInput,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { DatePickerModal } from "react-native-paper-dates";
import {
  DefaultTheme,
  DarkTheme,
  Provider as PaperProvider,
} from "react-native-paper";
import { Calendar } from "react-native-calendars";
import { View } from "../../../components/shared/Themed";
import { useAppTheme } from "../../../hooks/useAppTheme";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import {
  getAllAppointments,
  getWarehouseList,
} from "../../../redux/dispatcher/portalDispatcher";
import { useTranslation } from "../../../utils/useTranslation";

import Icon from "react-native-vector-icons/Feather";

interface Warehouse {
  _id: string;
  name: string;
  calendarId: string;
  timezone: string;
}

const FeatherIcon: React.ComponentType<any> = Icon as any;
// ─── Types ────────────────────────────────────────────────────────────────────
interface PortalAppointment {
  _id: string;
  calendarEventId: string;
  summary: string;
  description?: string;
  startDateTime: string;
  endDateTime: string;
  timeZone?: string;
  status: "SCHEDULED" | "CANCELLED" | "COMPLETED";
  notes?: string;
  organization?: string;
  createdBy?: { _id: string; auth?: { email?: string } };
  orderDetailId?: string;
  orderDetail?: {
    _id?: string;
    warehouse?: { _id?: string; calendarId?: string };
  };
}

interface CalendarEvent {
  id: string;
  summary: string;
  description?: string;
  startDateTime: string;
  endDateTime: string;
  status: string;
  notes?: string;
  calendarEventId: string;
  createdBy?: { email?: string };
  warehouseCalendarId?: string;
  raw: PortalAppointment;
}

type CalendarView = "month" | "week" | "day";

// ─── Constants ────────────────────────────────────────────────────────────────
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const STATUS_COLOR: Record<string, string> = {
  SCHEDULED: "#61B8FF",
  COMPLETED: "#16A765",
  CANCELLED: "#F87171",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getEventColor = (e: CalendarEvent) =>
  STATUS_COLOR[e.status] ?? STATUS_COLOR.SCHEDULED;

const parseDate = (s: string): Date => new Date(s);

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const formatTime = (d: Date) =>
  d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const formatDateLabel = (d: Date) =>
  `${DAYS_SHORT[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;

const startOfWeek = (d: Date): Date => {
  const result = new Date(d);
  result.setDate(d.getDate() - d.getDay());
  result.setHours(0, 0, 0, 0);
  return result;
};

const startOfMonth = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), 1);

/** Collapse events sharing the same order (summary) into one entry with a count */
const groupEventsBySummary = (
  evts: CalendarEvent[],
): (CalendarEvent & { count: number })[] => {
  const grouped: (CalendarEvent & { count: number })[] = [];
  const indexBySummary = new Map<string, number>();
  for (const e of evts) {
    const existingIdx = indexBySummary.get(e.summary);
    if (existingIdx !== undefined) {
      grouped[existingIdx].count += 1;
    } else {
      indexBySummary.set(e.summary, grouped.length);
      grouped.push({ ...e, count: 1 });
    }
  }
  return grouped;
};

/** Map raw PortalAppointment → CalendarEvent used by the UI */
const toCalendarEvent = (a: PortalAppointment): CalendarEvent => ({
  id: a._id,
  summary: a.summary,
  description: a.description,
  startDateTime: a.startDateTime,
  endDateTime: a.endDateTime,
  status: a.status,
  notes: a.notes,
  calendarEventId: a.calendarEventId,
  createdBy: { email: a.createdBy?.auth?.email },
  warehouseCalendarId: a.orderDetail?.warehouse?.calendarId,
  raw: a,
});

// ─── Sub-components ───────────────────────────────────────────────────────────
const StatusBadge = ({ status }: { status: string }) => {
  const { colors } = useAppTheme();
  const apptStyles = createStyles(colors);
  const color = STATUS_COLOR[status] ?? "#9CA3AF";
  return (
    <RNView
      style={[
        apptStyles.statusBadge,
        { backgroundColor: color + "22", borderColor: color },
      ]}
    >
      <RNView style={[apptStyles.statusDot, { backgroundColor: color }]} />
      <Text style={[apptStyles.statusText, { color }]}>{status}</Text>
    </RNView>
  );
};

const EventDetailModal = ({
  event,
  visible,
  onClose,
}: {
  event: CalendarEvent | null;
  visible: boolean;
  onClose: () => void;
}) => {
  const { colors } = useAppTheme();
  const apptStyles = createStyles(colors);
  if (!event) return null;
  const start = parseDate(event.startDateTime);
  const end = parseDate(event.endDateTime);
  const color = getEventColor(event);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable style={apptStyles.modalOverlay} onPress={onClose}>
        <Pressable
          style={apptStyles.eventDetailCard}
          onPress={(e) => e.stopPropagation()}
        >
          <RNView
            style={[apptStyles.eventDetailColorBar, { backgroundColor: color }]}
          />
          <ScrollView
            style={{ padding: 20 }}
            showsVerticalScrollIndicator={false}
          >
            <RNView style={{ marginBottom: 12 }}>
              <Text style={apptStyles.eventDetailTitle}>{event.summary}</Text>
              <StatusBadge status={event.status} />
            </RNView>
            <RNView style={apptStyles.eventDetailRow}>
              <FeatherIcon
                name="clock"
                size={14}
                color={colors.textSecondary}
              />
              <Text style={apptStyles.eventDetailMeta}>
                {formatDateLabel(start)}
                {"\n"}
                {formatTime(start)} – {formatTime(end)}
              </Text>
            </RNView>
            {!!event.description && (
              <RNView style={apptStyles.eventDetailRow}>
                <FeatherIcon
                  name="file-text"
                  size={14}
                  color={colors.textSecondary}
                />
                <Text style={apptStyles.eventDetailMeta}>
                  {event.description}
                </Text>
              </RNView>
            )}
            {!!event.notes && (
              <RNView style={apptStyles.eventDetailRow}>
                <FeatherIcon
                  name="edit-3"
                  size={14}
                  color={colors.textSecondary}
                />
                <Text style={apptStyles.eventDetailMeta}>{event.notes}</Text>
              </RNView>
            )}
            {!!event.createdBy?.email && (
              <RNView style={apptStyles.eventDetailRow}>
                <FeatherIcon
                  name="user"
                  size={14}
                  color={colors.textSecondary}
                />
                <Text style={apptStyles.eventDetailMeta}>
                  {event.createdBy.email}
                </Text>
              </RNView>
            )}
            {!!event.calendarEventId && (
              <>
                <RNView style={apptStyles.divider} />
                <Text style={apptStyles.sectionLabel}>
                  GOOGLE CALENDAR EVENT ID
                </Text>
                <Text style={[apptStyles.eventDetailMeta, { fontSize: 11 }]}>
                  {event.calendarEventId}
                </Text>
              </>
            )}
          </ScrollView>
          <TouchableOpacity style={apptStyles.closeBtn} onPress={onClose}>
            <FeatherIcon name="x" size={18} color={colors.textSecondary} />
          </TouchableOpacity>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const EventPill = ({
  event,
  compact = false,
  onPress,
}: {
  event: CalendarEvent;
  compact?: boolean;
  onPress: (e: CalendarEvent) => void;
}) => {
  const { colors } = useAppTheme();
  const apptStyles = createStyles(colors);
  const color = getEventColor(event);
  const start = parseDate(event.startDateTime);
  return (
    <TouchableOpacity
      style={[
        apptStyles.eventPill,
        { backgroundColor: color + "22", borderLeftColor: color },
        compact && apptStyles.eventPillCompact,
      ]}
      onPress={() => onPress(event)}
      activeOpacity={0.75}
      hitSlop={{ top: 5, bottom: 5, left: 5, right: 5 }}
    >
      {!compact && (
        <Text style={[apptStyles.eventPillTime, { color }]}>
          {formatTime(start)}
        </Text>
      )}
      <Text
        style={[
          apptStyles.eventPillTitle,
          { color },
          compact && { fontSize: 10 },
        ]}
        numberOfLines={compact ? 1 : 2}
      >
        {event.summary}
      </Text>
    </TouchableOpacity>
  );
};

const MonthView = ({
  currentDate,
  events,
  onDayPress,
  onEventPress,
  selectedDate,
}: {
  currentDate: Date;
  events: CalendarEvent[];
  onDayPress: (d: Date) => void;
  onEventPress: (e: CalendarEvent) => void;
  selectedDate: Date;
}) => {
  const { colors } = useAppTheme();
  const { isSmallDevice, isMobileDevice } = useContext(
    DeviceOrientationContext,
  );
  const apptStyles = createStyles(colors);
  const today = new Date();
  const startDay = startOfWeek(startOfMonth(currentDate));
  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(startDay);
    d.setDate(startDay.getDate() + i);
    return d;
  });

  const eventsOnDay = (d: Date) =>
    events.filter((e) => isSameDay(parseDate(e.startDateTime), d));

  return (
    <RNView>
      <RNView style={apptStyles.weekHeaderRow}>
        {DAYS_SHORT.map((d) => (
          <RNView key={d} style={apptStyles.weekHeaderCell}>
            <Text style={apptStyles.weekHeaderText}>{d}</Text>
          </RNView>
        ))}
      </RNView>
      <RNView style={apptStyles.monthGrid}>
        {cells.map((cell, idx) => {
          const inMonth = cell.getMonth() === currentDate.getMonth();
          const isToday = isSameDay(cell, today);
          const isSel = isSameDay(cell, selectedDate);
          const dayEvts = groupEventsBySummary(eventsOnDay(cell));
          return (
            <TouchableOpacity
              key={idx}
              style={[
                apptStyles.monthCell,
                isSel && apptStyles.monthCellSelected,
              ]}
              onPress={() => onDayPress(cell)}
              activeOpacity={0.7}
            >
              <RNView
                style={[
                  apptStyles.dayNumberWrap,
                  isToday && apptStyles.todayCircle,
                ]}
              >
                <Text
                  style={[
                    apptStyles.dayNumber,
                    !inMonth && apptStyles.dayNumberFaded,
                    isToday && apptStyles.dayNumberToday,
                  ]}
                >
                  {cell.getDate()}
                </Text>
              </RNView>
              {isSmallDevice || isMobileDevice ? (
                dayEvts.length > 0 && (
                  <RNView
                    style={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: 2,
                      justifyContent: "center",
                      marginTop: 4,
                      paddingHorizontal: 2,
                    }}
                  >
                    {dayEvts.slice(0, 4).map((ev, i) => (
                      <RNView
                        key={i}
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: getEventColor(ev),
                        }}
                      />
                    ))}
                    {dayEvts.length > 4 && (
                      <Text
                        style={{ fontSize: 8, color: colors.textSecondary }}
                      >
                        +
                      </Text>
                    )}
                  </RNView>
                )
              ) : (
                <>
                  {dayEvts.slice(0, 2).map((ev, i) => (
                    <EventPill
                      key={i}
                      event={ev}
                      compact
                      onPress={onEventPress}
                    />
                  ))}
                  {dayEvts.length > 2 && (
                    <Text style={apptStyles.moreEvents}>
                      +{dayEvts.length - 2} more
                    </Text>
                  )}
                </>
              )}
            </TouchableOpacity>
          );
        })}
      </RNView>
    </RNView>
  );
};

const WeekView = ({
  currentDate,
  events,
  onEventPress,
  onDayPress,
}: {
  currentDate: Date;
  events: CalendarEvent[];
  onEventPress: (e: CalendarEvent) => void;
  onDayPress: (d: Date) => void;
}) => {
  const { colors } = useAppTheme();
  const apptStyles = createStyles(colors);
  const today = new Date();
  const weekStart = startOfWeek(currentDate);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ minWidth: "100%" }}
    >
      <RNView style={{ flexDirection: "row", flex: 1 }}>
        {days.map((day, idx) => {
          const isToday = isSameDay(day, today);
          const dayEvts = groupEventsBySummary(
            events.filter((e) => isSameDay(parseDate(e.startDateTime), day)),
          );
          return (
            <RNView key={idx} style={apptStyles.weekDayColumn}>
              <TouchableOpacity
                style={[
                  apptStyles.weekDayHeader,
                  isToday && apptStyles.weekDayHeaderToday,
                ]}
                onPress={() => onDayPress(day)}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    apptStyles.weekDayName,
                    isToday && apptStyles.weekDayNameToday,
                  ]}
                >
                  {DAYS_SHORT[day.getDay()]}
                </Text>
                <Text
                  style={[
                    apptStyles.weekDayDate,
                    isToday && apptStyles.weekDayDateToday,
                  ]}
                >
                  {day.getDate()}
                </Text>
              </TouchableOpacity>
              <ScrollView
                style={apptStyles.weekDayEvents}
                showsVerticalScrollIndicator={false}
              >
                {dayEvts.length === 0 ? (
                  <Text style={apptStyles.noEventsText}>—</Text>
                ) : (
                  dayEvts.map((ev, i) => (
                    <EventPill key={i} event={ev} onPress={onEventPress} />
                  ))
                )}
              </ScrollView>
            </RNView>
          );
        })}
      </RNView>
    </ScrollView>
  );
};

const DayView = ({
  currentDate,
  events,
  onEventPress,
}: {
  currentDate: Date;
  events: CalendarEvent[];
  onEventPress: (e: CalendarEvent) => void;
}) => {
  const { colors } = useAppTheme();
  const apptStyles = createStyles(colors);
  const dayEvts = groupEventsBySummary(
    events.filter((e) => isSameDay(parseDate(e.startDateTime), currentDate)),
  );

  return (
    <ScrollView style={{ padding: 12 }} showsVerticalScrollIndicator={false}>
      {dayEvts.length === 0 ? (
        <RNView style={apptStyles.emptyDay}>
          <FeatherIcon name="calendar" size={36} color={colors.textSecondary} />
          <Text style={apptStyles.emptyDayText}>No appointments this day</Text>
        </RNView>
      ) : (
        dayEvts.map((ev, i) => {
          const start = parseDate(ev.startDateTime);
          const end = parseDate(ev.endDateTime);
          const color = getEventColor(ev);
          return (
            <TouchableOpacity
              key={i}
              style={[apptStyles.dayEventCard, { borderLeftColor: color }]}
              onPress={() => onEventPress(ev)}
              activeOpacity={0.75}
            >
              <RNView style={{ flex: 1 }}>
                <RNView
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    marginBottom: 4,
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <Text style={[apptStyles.dayEventTitle, { color }]}>
                    {ev.summary}
                  </Text>
                  <StatusBadge status={ev.status} />
                </RNView>
                <Text style={apptStyles.dayEventTime}>
                  {formatTime(start)} – {formatTime(end)}
                </Text>
                {!!ev.description && (
                  <RNView
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      marginTop: 4,
                      gap: 4,
                    }}
                  >
                    <FeatherIcon
                      name="file-text"
                      size={11}
                      color={colors.textSecondary}
                    />
                    <Text style={apptStyles.dayEventMeta} numberOfLines={1}>
                      {ev.description}
                    </Text>
                  </RNView>
                )}
                {!!ev.notes && (
                  <RNView
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      marginTop: 3,
                      gap: 4,
                    }}
                  >
                    <FeatherIcon
                      name="edit-3"
                      size={11}
                      color={colors.textSecondary}
                    />
                    <Text style={apptStyles.dayEventMeta} numberOfLines={1}>
                      {ev.notes}
                    </Text>
                  </RNView>
                )}
              </RNView>
              <FeatherIcon
                name="chevron-right"
                size={16}
                color={colors.textSecondary}
              />
            </TouchableOpacity>
          );
        })
      )}
    </ScrollView>
  );
};

// ─── Main Screen ──────────────────────────────────────────────────────────────

const DeliveryAppointmentsScreen = ({ route, navigation }: any) => {
  const { colors, isDark } = useAppTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch<any>(); //
  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);
  const apptStyles = createStyles(colors, isSmallDevice || isMobileDevice);

  const { userInfo, userRoles } = useSelector(
    (state: any) => state.configurations,
  );

  const canAccessAllAppointments =
    userRoles.some((role: string) => ["ADMIN", "OWNER"].includes(role)) ||
    userInfo?.team === "operation";

  const timezoneId = canAccessAllAppointments ? null : userInfo?.zone_info;

  // ── Redux state ───────────────────────────────────────────────────────────
  // appointments & loading come from portalSlice.appointments
  const { loading, appointments: rawAppointments } = useSelector(
    (state: any) => state.portal.appointments,
  );

  const { loading: warehouseLoading, warehouses } = useSelector(
    (state: any) => state.portal.warehouses,
  );

  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>("ALL");
  const [warehousePickerVisible, setWarehousePickerVisible] =
    useState<boolean>(false);

  useEffect(() => {
    if (canAccessAllAppointments) {
      dispatch(getWarehouseList());
    }
  }, [canAccessAllAppointments]);

  const selectedWarehouse = useMemo(
    () =>
      warehouses?.find((w: Warehouse) => w._id === selectedWarehouseId) ?? null,
    [warehouses, selectedWarehouseId],
  );

  const effectiveCalendarId = canAccessAllAppointments
    ? selectedWarehouseId === "ALL"
      ? null
      : (selectedWarehouse?.calendarId ?? null)
    : timezoneId;

  // ── Local UI state ────────────────────────────────────────────────────────
  const [view, setView] = useState<CalendarView>("month");
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [monthPickerVisible, setMonthPickerVisible] = useState<boolean>(false);
  const [weekPickerVisible, setWeekPickerVisible] = useState<boolean>(false);
  const [weekPickerDate, setWeekPickerDate] = useState<Date>(new Date());
  const [datePickerVisible, setDatePickerVisible] = useState<boolean>(false);
  const [pickerYear, setPickerYear] = useState<number>(
    new Date().getFullYear(),
  );
  const [eventModalVisible, setEventModalVisible] = useState<boolean>(false);

  // ── Map raw redux appointments → CalendarEvent[] ─────────────────────────
  // Memoised so it only recalculates when rawAppointments changes
  const allEvents: CalendarEvent[] = useMemo(() => {
    let mapped = (rawAppointments ?? []).map(toCalendarEvent);
    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      mapped = mapped.filter(
        (e) =>
          e.summary.toLowerCase().includes(q) ||
          e.description?.toLowerCase().includes(q) ||
          e.notes?.toLowerCase().includes(q),
      );
    }
    return mapped;
  }, [rawAppointments, searchQuery]);

  // ─── Fetch from backend ───────────────────────────────────────────────────
  const getDateWindow = (v: CalendarView, d: Date) => {
    const toDateString = (date: Date) =>
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

    let startDate: Date;
    let endDate: Date;

    if (v === "month") {
      startDate = new Date(d.getFullYear(), d.getMonth(), 1);
      endDate = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    } else if (v === "week") {
      startDate = startOfWeek(d);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
    } else {
      startDate = new Date(d);
      endDate = new Date(d);
    }

    // Pad by 7 days to avoid missing events at timezone boundaries
    startDate.setDate(startDate.getDate() - 7);
    endDate.setDate(endDate.getDate() + 7);

    return {
      startDate: toDateString(startDate),
      endDate: toDateString(endDate),
    };
  };

  const events: CalendarEvent[] = useMemo(() => {
    return allEvents.filter((e) => {
      const matchesCalendar =
        !timezoneId || e.warehouseCalendarId === timezoneId;
      return matchesCalendar;
    });
  }, [allEvents, timezoneId]);

  const load = async (
    showRefresh = false,
    v: CalendarView = view,
    d: Date = currentDate,
  ) => {
    if (showRefresh) setRefreshing(true);
    try {
      const { startDate, endDate } = getDateWindow(v, d);
      await dispatch(
        getAllAppointments({
          startDate,
          endDate,
          // non-admin: auto-filter by their own zone_info, unchanged
          ...(!canAccessAllAppointments && {
            timezone: timezoneId ?? undefined,
          }),
          // admin/owner: explicit warehouse filter via dropdown
          ...(canAccessAllAppointments &&
            selectedWarehouseId !== "ALL" && {
              calendarId: effectiveCalendarId ?? undefined,
            }),
        }),
      ).unwrap();
    } catch (err) {
      console.error("Failed to load appointments:", err);
    } finally {
      setRefreshing(false);
    }
  };

  // Re-fetch whenever view or currentDate changes
  useEffect(() => {
    load(false, view, currentDate);
  }, [view, currentDate, effectiveCalendarId]);

  // ── Calendar navigation ───────────────────────────────────────────────────
  const navigate = (direction: -1 | 1) => {
    const next = new Date(currentDate);
    if (view === "month") next.setMonth(next.getMonth() + direction);
    else if (view === "week") next.setDate(next.getDate() + direction * 7);
    else next.setDate(next.getDate() + direction);
    setCurrentDate(next);
  };

  const goToToday = () => {
    const now = new Date();
    setCurrentDate(now);
    setSelectedDate(now);
  };

  const getHeaderLabel = (): string => {
    if (view === "month")
      return `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    if (view === "week") {
      const ws = startOfWeek(currentDate);
      const we = new Date(ws);
      we.setDate(ws.getDate() + 6);
      return `${MONTHS[ws.getMonth()]} ${ws.getDate()} – ${we.getDate()}, ${ws.getFullYear()}`;
    }
    return formatDateLabel(currentDate);
  };

  const handleDayPress = (d: Date) => {
    setSelectedDate(d);
    setCurrentDate(d);
    setView("day");
    load(false, "day", d);
  };

  const handleEventPress = (e: CalendarEvent) => {
    setSelectedEvent(e);
    setEventModalVisible(true);
  };

  return (
    <PaperProvider
      theme={{
        ...(isDark ? DarkTheme : DefaultTheme),
        colors: {
          ...(isDark ? DarkTheme.colors : DefaultTheme.colors),
          primary: "#3B82F6",
        },
      }}
    >
      <View style={{ flex: 1, backgroundColor: colors.background }}>
        {/* ── Top bar ───────────────────────────────────────────────────────── */}
        <RNView style={apptStyles.headerBar}>
          <RNView style={apptStyles.headerLeft}>
            <TouchableOpacity
              onPress={() => navigate(-1)}
              style={apptStyles.navBtn}
            >
              <FeatherIcon name="chevron-left" size={18} color={colors.text} />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => navigate(1)}
              style={apptStyles.navBtn}
            >
              <FeatherIcon name="chevron-right" size={18} color={colors.text} />
            </TouchableOpacity>

            <TouchableOpacity
              onPress={() => {
                if (view === "month") {
                  setPickerYear(currentDate.getFullYear());
                  setMonthPickerVisible(true);
                } else if (view === "week") {
                  setWeekPickerDate(currentDate);
                  setWeekPickerVisible(true);
                } else {
                  setDatePickerVisible(true);
                }
              }}
              style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
            >
              <Text style={apptStyles.headerLabel}>{getHeaderLabel()}</Text>
              <FeatherIcon
                name="calendar"
                size={16}
                color={colors.textSecondary}
              />
            </TouchableOpacity>
          </RNView>

          <RNView style={apptStyles.headerRight}>
            <TouchableOpacity style={apptStyles.todayBtn} onPress={goToToday}>
              <Text style={apptStyles.todayBtnText}>{t("today")}</Text>
            </TouchableOpacity>
            <RNView style={apptStyles.viewSwitcher}>
              {(["month", "week", "day"] as CalendarView[]).map((v) => {
                let translationKey = "";
                if (v === "month") translationKey = "calendarMonth";
                if (v === "week") translationKey = "calendarWeek";
                if (v === "day") translationKey = "calendarDay";

                return (
                  <TouchableOpacity
                    key={v}
                    style={[
                      apptStyles.viewTab,
                      view === v && apptStyles.viewTabActive,
                    ]}
                    onPress={() => setView(v)}
                  >
                    <Text
                      style={[
                        apptStyles.viewTabText,
                        view === v && apptStyles.viewTabTextActive,
                      ]}
                    >
                      {t(translationKey)}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </RNView>
          </RNView>
        </RNView>

        {canAccessAllAppointments && (
          <RNView style={apptStyles.warehouseFilterWrap}>
            <TouchableOpacity
              style={apptStyles.warehouseBtn}
              onPress={() => setWarehousePickerVisible((prev) => !prev)}
            >
              <FeatherIcon name="map-pin" size={13} color={colors.text} />
              <Text style={apptStyles.warehouseBtnText} numberOfLines={1}>
                {selectedWarehouseId === "ALL"
                  ? "All Warehouses"
                  : (selectedWarehouse?.name ?? "Select")}
              </Text>
              <FeatherIcon
                name={warehousePickerVisible ? "chevron-up" : "chevron-down"}
                size={13}
                color={colors.textSecondary}
              />
            </TouchableOpacity>

            <RNView style={apptStyles.searchWrap}>
              <FeatherIcon
                name="search"
                size={14}
                color={colors.textSecondary}
              />
              <TextInput
                style={apptStyles.searchInput}
                placeholder="Search..."
                placeholderTextColor={colors.textSecondary}
                value={searchQuery}
                onChangeText={setSearchQuery}
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity
                  onPress={() => setSearchQuery("")}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <FeatherIcon
                    name="x"
                    size={14}
                    color={colors.textSecondary}
                  />
                </TouchableOpacity>
              )}
            </RNView>

            {warehousePickerVisible && (
              <>
                <Pressable
                  style={apptStyles.warehouseBackdrop}
                  onPress={() => setWarehousePickerVisible(false)}
                />
                <RNView style={apptStyles.warehouseDropdown}>
                  <ScrollView
                    nestedScrollEnabled
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator={false}
                  >
                    <TouchableOpacity
                      style={[
                        apptStyles.warehouseOption,
                        selectedWarehouseId === "ALL" &&
                          apptStyles.warehouseOptionActive,
                      ]}
                      onPress={() => {
                        setSelectedWarehouseId("ALL");
                        setWarehousePickerVisible(false);
                      }}
                    >
                      <Text
                        style={[
                          apptStyles.warehouseOptionText,
                          selectedWarehouseId === "ALL" &&
                            apptStyles.warehouseOptionTextActive,
                        ]}
                      >
                        All Warehouses
                      </Text>
                      {selectedWarehouseId === "ALL" && (
                        <FeatherIcon
                          name="check"
                          size={16}
                          color={colors.primary}
                        />
                      )}
                    </TouchableOpacity>

                    {warehouseLoading ? (
                      <ActivityIndicator
                        size="small"
                        color={colors.primary}
                        style={{ marginVertical: 12 }}
                      />
                    ) : (
                      (warehouses ?? []).map((w: Warehouse) => (
                        <TouchableOpacity
                          key={w._id}
                          style={[
                            apptStyles.warehouseOption,
                            selectedWarehouseId === w._id &&
                              apptStyles.warehouseOptionActive,
                          ]}
                          onPress={() => {
                            setSelectedWarehouseId(w._id);
                            setWarehousePickerVisible(false);
                          }}
                        >
                          <Text
                            style={[
                              apptStyles.warehouseOptionText,
                              selectedWarehouseId === w._id &&
                                apptStyles.warehouseOptionTextActive,
                            ]}
                          >
                            {w.name}
                          </Text>
                          {selectedWarehouseId === w._id && (
                            <FeatherIcon
                              name="check"
                              size={16}
                              color={colors.primary}
                            />
                          )}
                        </TouchableOpacity>
                      ))
                    )}
                  </ScrollView>
                </RNView>
              </>
            )}
          </RNView>
        )}

        {/* ── Calendar ──────────────────────────────────────────────────────── */}
        <RNView style={apptStyles.body}>
          {loading && !refreshing ? (
            <RNView style={apptStyles.centered}>
              <ActivityIndicator size="large" color={colors.primary} />
              <Text style={apptStyles.loadingText}>Loading appointments…</Text>
            </RNView>
          ) : (
            <ScrollView
              style={{ flex: 1 }}
              showsVerticalScrollIndicator={false}
              scrollEnabled={view !== "week"}
              refreshControl={
                <RefreshControl
                  refreshing={refreshing}
                  onRefresh={() => load(true)}
                  colors={["#61B8FF"]}
                  tintColor="#61B8FF"
                />
              }
            >
              {view === "month" && (
                <MonthView
                  currentDate={currentDate}
                  events={events}
                  onDayPress={handleDayPress}
                  onEventPress={handleEventPress}
                  selectedDate={selectedDate}
                />
              )}
              {view === "week" && (
                <WeekView
                  currentDate={currentDate}
                  events={events}
                  onEventPress={handleEventPress}
                  onDayPress={handleDayPress}
                />
              )}
              {view === "day" && (
                <DayView
                  currentDate={currentDate}
                  events={events}
                  onEventPress={handleEventPress}
                />
              )}
            </ScrollView>
          )}
        </RNView>

        <Modal
          visible={monthPickerVisible}
          transparent
          animationType="fade"
          onRequestClose={() => setMonthPickerVisible(false)}
        >
          <RNView
            style={{
              flex: 1,
              backgroundColor: "rgba(0,0,0,0.5)",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <RNView
              style={{
                backgroundColor: colors.surface,
                padding: 20,
                borderRadius: 12,
                width: 320,
              }}
            >
              <RNView
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <TouchableOpacity
                  onPress={() => setPickerYear(pickerYear - 1)}
                  style={{ padding: 10 }}
                >
                  <FeatherIcon
                    name="chevron-left"
                    size={20}
                    color={colors.text}
                  />
                </TouchableOpacity>
                <Text
                  style={{
                    fontSize: 18,
                    fontWeight: "700",
                    color: colors.text,
                  }}
                >
                  {pickerYear}
                </Text>
                <TouchableOpacity
                  onPress={() => setPickerYear(pickerYear + 1)}
                  style={{ padding: 10 }}
                >
                  <FeatherIcon
                    name="chevron-right"
                    size={20}
                    color={colors.text}
                  />
                </TouchableOpacity>
              </RNView>
              <RNView
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                }}
              >
                {MONTHS.map((month, idx) => (
                  <TouchableOpacity
                    key={idx}
                    style={{
                      width: "30%",
                      paddingVertical: 12,
                      alignItems: "center",
                      marginBottom: 10,
                      borderRadius: 8,
                      backgroundColor:
                        currentDate.getMonth() === idx &&
                        currentDate.getFullYear() === pickerYear
                          ? colors.primary
                          : colors.surfaceSubtle,
                    }}
                    onPress={() => {
                      const newDate = new Date(pickerYear, idx, 1);
                      setCurrentDate(newDate);
                      setMonthPickerVisible(false);
                      if (view !== "month") setView("month");
                    }}
                  >
                    <Text
                      style={{
                        color:
                          currentDate.getMonth() === idx &&
                          currentDate.getFullYear() === pickerYear
                            ? "#fff"
                            : colors.text,
                      }}
                    >
                      {month.substring(0, 3)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </RNView>
              <TouchableOpacity
                onPress={() => setMonthPickerVisible(false)}
                style={{
                  marginTop: 10,
                  alignItems: "center",
                  paddingVertical: 10,
                }}
              >
                <Text style={{ color: colors.textSecondary }}>Cancel</Text>
              </TouchableOpacity>
            </RNView>
          </RNView>
        </Modal>

        <Modal
          visible={weekPickerVisible}
          transparent
          animationType="fade"
          onRequestClose={() => setWeekPickerVisible(false)}
        >
          <Pressable
            style={{
              flex: 1,
              backgroundColor: "rgba(0,0,0,0.5)",
              justifyContent: "center",
              alignItems: "center",
            }}
            onPress={() => setWeekPickerVisible(false)}
          >
            <Pressable
              style={{
                backgroundColor: colors.surface,
                borderRadius: 16,
                width: 340,
                overflow: "hidden",
              }}
              onPress={(e) => e.stopPropagation()}
            >
              {/* Title */}
              <RNView
                style={{
                  paddingVertical: 14,
                  paddingHorizontal: 20,
                  borderBottomWidth: 1,
                  borderBottomColor: colors.border,
                }}
              >
                <Text
                  style={{
                    fontSize: 15,
                    fontWeight: "700",
                    color: colors.text,
                    textAlign: "center",
                  }}
                >
                  Select Week
                </Text>
              </RNView>

              {/* Year selector */}
              <RNView
                style={{
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 24,
                  paddingVertical: 12,
                }}
              >
                <TouchableOpacity
                  onPress={() =>
                    setWeekPickerDate(
                      new Date(
                        weekPickerDate.getFullYear() - 1,
                        weekPickerDate.getMonth(),
                        15,
                      ),
                    )
                  }
                  style={{ padding: 6 }}
                >
                  <FeatherIcon
                    name="chevron-left"
                    size={18}
                    color={colors.textSecondary}
                  />
                </TouchableOpacity>
                <Text
                  style={{
                    fontSize: 16,
                    fontWeight: "700",
                    color: colors.text,
                    minWidth: 50,
                    textAlign: "center",
                  }}
                >
                  {weekPickerDate.getFullYear()}
                </Text>
                <TouchableOpacity
                  onPress={() =>
                    setWeekPickerDate(
                      new Date(
                        weekPickerDate.getFullYear() + 1,
                        weekPickerDate.getMonth(),
                        15,
                      ),
                    )
                  }
                  style={{ padding: 6 }}
                >
                  <FeatherIcon
                    name="chevron-right"
                    size={18}
                    color={colors.textSecondary}
                  />
                </TouchableOpacity>
              </RNView>

              {/* Month grid */}
              <RNView
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  paddingHorizontal: 16,
                  marginBottom: 8,
                }}
              >
                {MONTHS.map((month, idx) => {
                  const isActive = weekPickerDate.getMonth() === idx;
                  return (
                    <TouchableOpacity
                      key={idx}
                      style={{
                        width: "25%",
                        paddingVertical: 8,
                        alignItems: "center",
                      }}
                      onPress={() => {
                        setWeekPickerDate(
                          new Date(weekPickerDate.getFullYear(), idx, 15),
                        );
                      }}
                    >
                      <RNView
                        style={{
                          paddingVertical: 6,
                          paddingHorizontal: 14,
                          borderRadius: 20,
                          backgroundColor: isActive
                            ? colors.primary
                            : "transparent",
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 12,
                            fontWeight: "600",
                            color: isActive ? "#fff" : colors.textSecondary,
                          }}
                        >
                          {month.substring(0, 3)}
                        </Text>
                      </RNView>
                    </TouchableOpacity>
                  );
                })}
              </RNView>

              {/* Divider */}
              <RNView
                style={{
                  height: 1,
                  backgroundColor: colors.border,
                  marginHorizontal: 16,
                }}
              />

              {/* Calendar */}
              <Calendar
                key={`${weekPickerDate.getFullYear()}-${weekPickerDate.getMonth()}`}
                current={`${weekPickerDate.getFullYear()}-${String(weekPickerDate.getMonth() + 1).padStart(2, "0")}-15`}
                hideArrows={true}
                renderHeader={() => null}
                markingType={"period"}
                markedDates={(() => {
                  const start = startOfWeek(currentDate);
                  const marks: any = {};
                  for (let i = 0; i < 7; i++) {
                    const d = new Date(start);
                    d.setDate(start.getDate() + i);
                    const ds = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                    marks[ds] = {
                      startingDay: i === 0,
                      endingDay: i === 6,
                      color: colors.primary,
                      textColor: "#fff",
                    };
                  }
                  return marks;
                })()}
                onDayPress={(day: any) => {
                  const d = new Date(day.year, day.month - 1, day.day);
                  setCurrentDate(d);
                  setWeekPickerVisible(false);
                }}
                theme={{
                  calendarBackground: colors.surface,
                  textSectionTitleColor: colors.textSecondary,
                  dayTextColor: colors.text,
                  textDisabledColor: isDark ? "#6B7280" : "#D1D5DB",
                  monthTextColor: colors.text,
                  arrowColor: colors.primary,
                  todayTextColor: colors.primary,
                }}
              />

              {/* Cancel button */}
              <TouchableOpacity
                onPress={() => setWeekPickerVisible(false)}
                style={{
                  paddingVertical: 14,
                  borderTopWidth: 1,
                  borderTopColor: colors.border,
                }}
              >
                <Text
                  style={{
                    color: colors.textSecondary,
                    textAlign: "center",
                    fontWeight: "600",
                    fontSize: 14,
                  }}
                >
                  Cancel
                </Text>
              </TouchableOpacity>
            </Pressable>
          </Pressable>
        </Modal>

        <EventDetailModal
          event={selectedEvent}
          visible={eventModalVisible}
          onClose={() => setEventModalVisible(false)}
        />

        <DatePickerModal
          locale="en-AU"
          mode="single"
          visible={datePickerVisible}
          onDismiss={() => setDatePickerVisible(false)}
          date={currentDate}
          onConfirm={(params: any) => {
            setDatePickerVisible(false);
            if (params.date) {
              setCurrentDate(params.date);
              if (view === "day") {
                setSelectedDate(params.date);
              }
            }
          }}
        />
      </View>
    </PaperProvider>
  );
};

export default DeliveryAppointmentsScreen;

// ─── Styles ───────────────────────────────────────────────────────────────────

const createStyles = (colors: any, isMobile?: boolean) =>
  StyleSheet.create({
    headerBar: {
      flexDirection: isMobile ? "column" : "row",
      alignItems: isMobile ? "flex-start" : "center",
      justifyContent: isMobile ? "flex-start" : "space-between",
      backgroundColor: colors.surface,
      paddingHorizontal: 14,
      paddingVertical: 10,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      flexWrap: "wrap",
      gap: isMobile ? 12 : 8,
    },
    headerLeft: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
    },
    headerRight: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
    },
    navBtn: {
      width: 32,
      height: 32,
      borderRadius: 8,
      backgroundColor: colors.surfaceSubtle,
      justifyContent: "center",
      alignItems: "center",
      borderWidth: 1,
      borderColor: colors.border,
    },
    headerLabel: {
      fontSize: 15,
      fontWeight: "700",
      color: colors.text,
      marginLeft: 6,
    },
    todayBtn: {
      paddingVertical: 6,
      paddingHorizontal: 14,
      borderRadius: 999,
      backgroundColor: colors.surfaceSubtle,
      borderWidth: 1,
      borderColor: colors.border,
    },
    todayBtnText: { fontSize: 13, fontWeight: "600", color: colors.text },
    viewSwitcher: {
      flexDirection: "row",
      backgroundColor: colors.surfaceSubtle,
      borderRadius: 8,
      padding: 3,
      borderWidth: 1,
      borderColor: colors.border,
    },
    viewTab: { paddingVertical: 5, paddingHorizontal: 12, borderRadius: 6 },
    viewTabActive: { backgroundColor: colors.primary },
    viewTabText: {
      fontSize: 12,
      fontWeight: "600",
      color: colors.textSecondary,
    },
    viewTabTextActive: { color: colors.surface },

    filterBar: {
      backgroundColor: colors.surface,
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    filterChip: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 5,
      paddingHorizontal: 12,
      backgroundColor: colors.surfaceSubtle,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: colors.border,
      gap: 6,
    },
    filterChipDot: { width: 7, height: 7, borderRadius: 4 },
    filterChipText: {
      fontSize: 12,
      fontWeight: "600",
      color: colors.textSecondary,
    },
    filterChipTextActive: { color: colors.surface },
    filterChipCount: {
      backgroundColor: "#E2E8F0",
      borderRadius: 999,
      paddingHorizontal: 6,
      paddingVertical: 1,
    },
    filterChipCountText: {
      fontSize: 10,
      fontWeight: "700",
      color: colors.textSecondary,
    },

    body: { flex: 1, backgroundColor: colors.surface },
    centered: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      padding: 32,
      gap: 12,
    },
    loadingText: { fontSize: 14, color: colors.textSecondary, marginTop: 8 },

    weekHeaderRow: {
      flexDirection: "row",
      backgroundColor: colors.surfaceSubtle,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    weekHeaderCell: { flex: 1, alignItems: "center", paddingVertical: 8 },
    weekHeaderText: {
      fontSize: 11,
      fontWeight: "700",
      color: colors.textSecondary,
      textTransform: "uppercase",
      letterSpacing: 0.5,
    },

    monthGrid: { flexDirection: "row", flexWrap: "wrap" },
    monthCell: {
      width: `${100 / 7}%` as any,
      minHeight: 90,
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: colors.border,
      padding: 4,
      backgroundColor: colors.surface,
    },
    monthCellSelected: { backgroundColor: colors.primaryBg },
    dayNumberWrap: {
      width: 26,
      height: 26,
      borderRadius: 13,
      justifyContent: "center",
      alignItems: "center",
      marginBottom: 2,
    },
    todayCircle: { backgroundColor: colors.primary },
    dayNumber: { fontSize: 12, fontWeight: "600", color: colors.text },
    dayNumberFaded: { color: colors.textSecondary },
    dayNumberToday: { color: colors.surface },
    moreEvents: {
      fontSize: 9,
      color: colors.textSecondary,
      fontWeight: "600",
      marginTop: 1,
      paddingLeft: 2,
    },

    weekDayColumn: {
      flex: 1,
      minWidth: 130,
      borderRightWidth: 1,
      borderColor: colors.border,
      minHeight: 400,
    },
    weekDayHeader: {
      alignItems: "center",
      paddingVertical: 10,
      borderBottomWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceSubtle,
    },
    weekDayHeaderToday: { backgroundColor: colors.primaryBg },
    weekDayName: {
      fontSize: 11,
      fontWeight: "700",
      color: colors.textSecondary,
      textTransform: "uppercase",
      letterSpacing: 0.5,
    },
    weekDayNameToday: { color: colors.primary },
    weekDayDate: {
      fontSize: 20,
      fontWeight: "700",
      color: colors.text,
      marginTop: 2,
    },
    weekDayDateToday: { color: colors.primary },
    weekDayEvents: { padding: 6, maxHeight: 500 },
    noEventsText: {
      textAlign: "center",
      color: colors.textSecondary,
      fontSize: 20,
      marginTop: 24,
    },

    dayEventCard: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: colors.surfaceSubtle,
      borderRadius: 10,
      borderLeftWidth: 4,
      paddingVertical: 12,
      paddingHorizontal: 14,
      marginBottom: 10,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.04,
      shadowRadius: 4,
      elevation: 1,
    },
    dayEventTitle: { fontSize: 14, fontWeight: "700", marginBottom: 3 },
    dayEventTime: {
      fontSize: 12,
      color: colors.textSecondary,
      fontWeight: "500",
    },
    dayEventMeta: { fontSize: 11, color: colors.textSecondary, flex: 1 },
    emptyDay: { alignItems: "center", paddingTop: 60, gap: 12 },
    emptyDayText: {
      fontSize: 14,
      color: colors.textSecondary,
      fontWeight: "500",
    },

    eventPill: {
      borderLeftWidth: 3,
      borderRadius: 4,
      paddingVertical: 3,
      paddingHorizontal: 5,
      marginBottom: 3,
    },
    eventPillCompact: { paddingVertical: 2, paddingHorizontal: 4 },
    eventPillTime: { fontSize: 10, fontWeight: "700", marginBottom: 1 },
    eventPillTitle: { fontSize: 11, fontWeight: "600" },

    statusBadge: {
      flexDirection: "row",
      alignItems: "center",
      alignSelf: "flex-start",
      paddingVertical: 3,
      paddingHorizontal: 8,
      borderRadius: 999,
      borderWidth: 1,
      gap: 5,
      marginTop: 4,
    },
    statusDot: { width: 6, height: 6, borderRadius: 3 },
    statusText: {
      fontSize: 10,
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.5,
    },

    modalOverlay: {
      flex: 1,
      backgroundColor: "rgba(0,0,0,0.3)",
      justifyContent: "center",
      alignItems: "center",
      padding: 20,
    },
    eventDetailCard: {
      width: "100%",
      maxWidth: 420,
      backgroundColor: colors.surface,
      borderRadius: 16,
      maxHeight: "85%",
      overflow: "hidden",
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 16 },
      shadowOpacity: 0.15,
      shadowRadius: 32,
      elevation: 16,
    },
    eventDetailColorBar: { height: 6, width: "100%" },
    eventDetailTitle: {
      fontSize: 18,
      fontWeight: "800",
      color: colors.text,
      lineHeight: 24,
    },
    eventDetailRow: {
      flexDirection: "row",
      alignItems: "flex-start",
      marginBottom: 10,
      gap: 8,
    },
    eventDetailMeta: {
      fontSize: 13,
      color: colors.textSecondary,
      flex: 1,
      lineHeight: 18,
    },
    divider: {
      height: 1,
      backgroundColor: colors.surfaceSubtle,
      marginVertical: 12,
    },
    sectionLabel: {
      fontSize: 10,
      fontWeight: "700",
      color: colors.textSecondary,
      letterSpacing: 1,
      marginBottom: 6,
    },
    closeBtn: {
      position: "absolute",
      top: 14,
      right: 14,
      width: 30,
      height: 30,
      borderRadius: 15,
      backgroundColor: colors.surfaceSubtle,
      justifyContent: "center",
      alignItems: "center",
    },

    warehouseBtn: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 6,
      paddingHorizontal: 12,
      borderRadius: 999,
      backgroundColor: colors.surfaceSubtle,
      borderWidth: 1,
      borderColor: colors.border,
      gap: 6,
      maxWidth: 160,
    },
    warehouseBtnText: {
      fontSize: 12,
      fontWeight: "600",
      color: colors.text,
      flexShrink: 1,
    },
    warehousePickerCard: {
      width: "100%",
      maxWidth: 360,
      maxHeight: "70%",
      backgroundColor: colors.surface,
      borderRadius: 16,
      padding: 16,
    },
    warehouseOption: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingVertical: 12,
      paddingHorizontal: 10,
      borderRadius: 8,
    },
    warehouseOptionActive: { backgroundColor: colors.primaryBg },
    warehouseOptionText: {
      fontSize: 14,
      fontWeight: "600",
      color: colors.text,
    },
    warehouseOptionTextActive: { color: colors.primary },

    warehouseFilterWrap: {
      position: "relative",
      backgroundColor: colors.surface,
      paddingHorizontal: 14,
      paddingVertical: 10,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      zIndex: 10,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    searchWrap: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: colors.surfaceSubtle,
      borderRadius: 8,
      paddingHorizontal: 12,
      height: 34,
      flex: isMobile ? 1 : undefined,
      width: isMobile ? undefined : 250,
      marginLeft: 12,
    },
    searchInput: {
      flex: 1,
      marginLeft: 8,
      color: colors.text,
      fontSize: 13,
      padding: 0,
    },
    warehouseBackdrop: {
      position: "absolute",
      top: -1000,
      left: -1000,
      right: -1000,
      bottom: -2000,
      zIndex: 40,
    },
    warehouseDropdown: {
      position: "absolute",
      top: "100%",
      left: 14,
      marginTop: -2,
      width: 220,
      backgroundColor: colors.surface,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border,
      paddingVertical: 6,
      paddingHorizontal: 6,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 12,
      elevation: 8,
      zIndex: 50,
      maxHeight: 280,
    },
  });
